import type { StatusChipVariant } from '@/components/ui/status-chip';
import type { SurveillanceClientType, SurveillanceStatus } from '@/services/api/market-surveillance';
import { formatSnakeCase } from '@/utils/string';

export const STATUS_LABELS: Record<SurveillanceStatus, string> = {
  OPEN: 'Open',
  INVESTIGATING: 'Investigating',
  ESCALATED: 'Escalated',
  CLOSED_FP: 'False Positive',
  CLOSED_TP: 'True Positive',
  CLOSED_ADJUDICATED: 'Adjudicated',
  STOR_FILED: 'STOR Filed',
};

export const STATUS_CHIP_VARIANTS: Record<SurveillanceStatus, StatusChipVariant> = {
  OPEN: 'info',
  INVESTIGATING: 'warning',
  ESCALATED: 'destructive',
  CLOSED_FP: 'muted',
  CLOSED_TP: 'success',
  CLOSED_ADJUDICATED: 'muted',
  STOR_FILED: 'destructive',
};

export const CLIENT_TYPE_LABELS: Record<SurveillanceClientType, string> = {
  MARKET_MAKER: 'Market Maker',
  RETAIL: 'Retail',
};

export const CLIENT_TYPE_CHIP_VARIANTS: Record<SurveillanceClientType, StatusChipVariant> = {
  MARKET_MAKER: 'info',
  RETAIL: 'muted',
};

/** Why an alert that narc stored never reached Slack */
export const notInSlackReason = (suppressedBy: string | null): string =>
  suppressedBy ? `Suppressed by ${suppressedBy} — not posted to Slack` : 'Triage score below the Slack threshold — not posted to Slack';

/** The analyst queue — the list's default status filter */
export const DEFAULT_OPEN_STATUSES: readonly SurveillanceStatus[] = ['OPEN', 'INVESTIGATING', 'ESCALATED'];

// Everything outside the queue counts as closed — STOR_FILED included (terminal, filed with the regulator)
export const isClosedStatus = (status: SurveillanceStatus): boolean => !DEFAULT_OPEN_STATUSES.includes(status);

export const getRiskScoreClass = (score: number): string => {
  if (score >= 75) return 'text-red-500';
  if (score >= 50) return 'text-amber-500';
  return 'text-emerald-500';
};

// Record<string, string> on purpose: narc adds typologies faster than this UI ships,
// so unknown values must render via the formatSnakeCase fallback, not break a Record type.
export const TYPOLOGY_LABELS: Record<string, string> = {
  QUOTE_STUFFING: 'Quote Stuffing',
  LAYERING_V2: 'Layering',
  WASH_TRADE_ABA: 'Wash Trading (A-B-A)',
  CAPITAL_LIMITS: 'Capital Limits',
  MARKING_THE_CLOSE: 'Marking the Close',
  MOMENTUM_IGNITION: 'Momentum Ignition',
  PAINTING_THE_TAPE: 'Painting the Tape',
  ABUSIVE_SQUEEZE: 'Abusive Squeeze',
  MATCHED_ORDERS: 'Matched Orders',
  ORDER_TO_TRADE_RATIO: 'Order-to-Trade Ratio',
  ABUSIVE_CANCELLATION: 'Abusive Cancellation',
  UNUSUAL_VOLUME: 'Unusual Volume',
  POSITION_LIMIT_BREACH: 'Position Limit Breach',
  ADVANCING_THE_BID: 'Advancing the Bid',
  SMOKING: 'Smoking',
  BOOK_REPLENISHMENT: 'Book Replenishment',
};

export const formatTypology = (value: string): string => TYPOLOGY_LABELS[value] ?? formatSnakeCase(value);

/** One-line investigator-facing meaning per transition, shown in the status menu */
export const STATUS_ACTION_DESCRIPTIONS: Record<SurveillanceStatus, string> = {
  OPEN: 'Return the alert to the open queue',
  INVESTIGATING: 'Take the alert under active review',
  ESCALATED: 'Hand to senior review',
  CLOSED_FP: 'Close — activity assessed as benign',
  CLOSED_TP: 'Close — market abuse confirmed',
  CLOSED_ADJUDICATED: 'Close — adjudicated outcome recorded',
  STOR_FILED: 'Suspicious transaction report filed with the regulator',
};

// Menu dots reuse the chip variant semantics so status colour has ONE source of truth.
const VARIANT_DOT_CLASSES: Record<StatusChipVariant, string> = {
  info: 'bg-sky-400',
  warning: 'bg-amber-400',
  destructive: 'bg-red-400',
  success: 'bg-emerald-400',
  muted: 'bg-zinc-400',
};

export const statusDotClass = (status: SurveillanceStatus): string => VARIANT_DOT_CLASSES[STATUS_CHIP_VARIANTS[status]];

/** Fixed menu order — working statuses first, closures next, reopen last */
const STATUS_ACTION_ORDER: readonly SurveillanceStatus[] = ['INVESTIGATING', 'ESCALATED', 'CLOSED_FP', 'CLOSED_TP', 'CLOSED_ADJUDICATED', 'STOR_FILED', 'OPEN'];

export interface SurveillanceStatusAction {
  status: SurveillanceStatus;
  label: string;
}

export const orderStatusActions = (allowed: readonly SurveillanceStatus[]): SurveillanceStatusAction[] =>
  STATUS_ACTION_ORDER.filter((status) => allowed.includes(status)).map((status) => ({
    status,
    label: STATUS_LABELS[status],
  }));
